import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Star, ExternalLink, PenLine } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent } from './ui/card';
import { Avatar, AvatarImage, AvatarFallback } from './ui/avatar';
import { Button } from './ui/button';

export const WRITE_REVIEW_URL = 'https://search.google.com/local/writereview?placeid=ChIJU9eD9grV0jsRwRmY9HGxYxM';

const API = process.env.REACT_APP_BACKEND_URL;

const Stars = ({ rating, size = 18 }) => (
  <div className="flex gap-0.5" aria-label={`${rating} stars`}>
    {[1, 2, 3, 4, 5].map((i) => (
      <Star
        key={i}
        size={size}
        className={i <= Math.round(rating) ? 'fill-amber-400 text-amber-400' : 'text-slate-300'}
      />
    ))}
  </div>
);

const GOOGLE_ICON_SIZE = 22;

const GoogleLogo = ({ size = GOOGLE_ICON_SIZE }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" aria-hidden="true">
    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
  </svg>
);

export const useGoogleReviews = () => {
  const { i18n } = useTranslation();
  const lang = (i18n.language || 'en').split('-')[0];
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    let cancelled = false;
    fetch(`${API}/api/reviews?lang=${lang}`)
      .then((r) => (r.ok ? r.json() : Promise.reject()))
      .then((d) => { if (!cancelled) setData(d); })
      .catch(() => { if (!cancelled) setError(true); });
    return () => { cancelled = true; };
  }, [lang]);

  return { data, error };
};

export const ReviewCard = ({ review }) => (
  <Card className="border-0 shadow-lg h-full" style={{ boxShadow: '0 8px 30px rgba(5, 56, 39, 0.1)' }} data-testid="review-card">
    <CardContent className="pt-6 flex flex-col h-full">
      <div className="flex items-center gap-3 mb-4">
        <Avatar className="w-11 h-11">
          <AvatarImage src={review.profile_photo} alt={review.author} referrerPolicy="no-referrer" />
          <AvatarFallback className="text-white font-bold" style={{ backgroundColor: 'var(--medical-primary)' }}>
            {review.author?.charAt(0)}
          </AvatarFallback>
        </Avatar>
        <div className="min-w-0">
          <p className="font-semibold text-slate-900 truncate">{review.author}</p>
          <p className="text-xs text-slate-500">{review.relative_time}</p>
        </div>
        <div className="ml-auto flex-shrink-0"><GoogleLogo size={18} /></div>
      </div>
      <Stars rating={review.rating} size={16} />
      {review.text && (
        <p className="text-slate-600 text-sm leading-relaxed mt-3 whitespace-pre-line">{review.text}</p>
      )}
    </CardContent>
  </Card>
);

export const ReviewsSummary = ({ data, light = false }) => {
  const { t } = useTranslation();
  return (
    <div className="flex flex-col sm:flex-row items-center justify-center gap-4 sm:gap-6" data-testid="reviews-summary">
      <div className="flex items-center gap-3">
        <GoogleLogo size={32} />
        <span className={`text-5xl font-bold ${light ? 'text-white' : 'text-slate-900'}`}>{data.rating?.toFixed(1)}</span>
        <Stars rating={data.rating} size={24} />
      </div>
      <p className={light ? 'text-emerald-100' : 'text-slate-600'}>
        {data.total_reviews} {t('reviews.googleReviews', 'Google Reviews')}
      </p>
    </div>
  );
};

export const ReviewActions = () => {
  const { t } = useTranslation();
  return (
    <div className="flex flex-col sm:flex-row gap-4 justify-center">
      <a href={WRITE_REVIEW_URL} target="_blank" rel="noopener noreferrer" data-testid="write-review-btn">
        <Button
          size="lg"
          className="w-full sm:w-auto text-base px-8 shadow-xl hover:scale-105 transition-all duration-300"
          style={{ backgroundColor: 'var(--medical-primary)' }}
        >
          <PenLine className="mr-2" size={18} />
          {t('reviews.writeReview', 'Write a Review on Google')}
        </Button>
      </a>
    </div>
  );
};

const GoogleReviewsSection = () => {
  const { t } = useTranslation();
  const { data, error } = useGoogleReviews();
  const reviews = data?.reviews;
  const topReviews = useMemo(
    () => (reviews || []).filter((r) => r.text).slice(0, 3),
    [reviews]
  );

  if (error && !data) return null;

  return (
    <section id="reviews" className="py-20 px-4 bg-slate-50" data-testid="google-reviews-section">
      <div className="container mx-auto max-w-6xl">
        <div className="text-center mb-12">
          <h2 className="text-4xl font-bold mb-3" style={{ color: 'var(--medical-primary)' }}>
            {t('reviews.title', 'What Our Patients Say')}
          </h2>
          <div className="w-16 h-1 mx-auto mb-6" style={{ backgroundColor: 'var(--medical-primary)' }} />
          {data ? (
            <ReviewsSummary data={data} />
          ) : (
            <p className="text-slate-500">{t('reviews.subtitle', 'Real patient reviews from Google')}</p>
          )}
        </div>

        {data && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
            {topReviews.map((review) => (
              <ReviewCard key={`${review.author}-${review.relative_time}`} review={review} />
            ))}
          </div>
        )}

        <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
          <ReviewActions />
          <Link to="/reviews" data-testid="view-all-reviews-link">
            <Button
              variant="outline"
              size="lg"
              className="text-base px-8 border-2 hover:scale-105 transition-all duration-300"
              style={{ borderColor: 'var(--medical-primary)', color: 'var(--medical-primary)' }}
            >
              {t('reviews.viewAll', 'View All Reviews')}
              <ExternalLink className="ml-2" size={16} />
            </Button>
          </Link>
        </div>
      </div>
    </section>
  );
};

export default GoogleReviewsSection;
